/**
 * The ONLY channel between Phaser and React. No per-frame traffic crosses it —
 * the game emits one-shot lifecycle signals (`runstart` when the player begins a
 * run by dragging the teased ball, `gameover` when it ends); React sends discrete
 * commands (start/restart/pause/resume). Live score / ball position stay inside
 * the Phaser canvas.
 *
 * START FLOW (docs: owner's drag-to-start spec): on the menu the live ball is
 * teased behind the React menu. The MOMENT the player begins dragging it,
 * {@link StartFlowScene} emits `runstart` ONCE (with the side they're leaning to,
 * if any). React reacts by elegantly animating its menu UI away and switching to
 * the playing screen; the countdown + run continue inside Phaser. This is a
 * one-shot boundary signal, never per-frame.
 *
 * Deliberately free of Phaser/React imports so neither side leaks into the other.
 * It MAY reference pure `core/` types (which import nothing app-specific).
 */
import type { AttackFamily, GameMode, RunRecord } from '../core/types';

/**
 * Emitted ONCE by {@link StartFlowScene} the instant the player starts dragging
 * the teased ball (i.e. begins a run). It is NOT the final mode commit — the dwell
 * logic still decides solo/multiplayer afterwards — it's the cue for React to
 * dismiss the menu and reveal the game. The optional `side` is a hint of which way
 * they're leaning at drag-start (purely for any directional flourish).
 */
export interface RunStartPayload {
  side: 'left' | 'right' | null;
}

export interface GameOverPayload {
  score: number;
  /**
   * Which mode the finished run was (solo / multiplayer). App.tsx routes the
   * summary by mode.
   */
  mode: GameMode;
  /**
   * The attack family that landed the kill (for the death-type stat / donut), or
   * null if the run ended without a hit.
   */
  deathFamily: AttackFamily | null;
  /**
   * The replayable {@link RunRecord} (seed + input timeline) for this run. App.tsx
   * submits it via `services.submitRun(record)` — the server re-simulates it to
   * compute the authoritative score (anti-cheat) and store the replay.
   */
  record: RunRecord;
}

/**
 * Discrete React→game commands. `start` is the Play-button FALLBACK — it commits
 * the run (solo) without a drag, so the game is always playable even if the
 * drag-to-start gesture isn't discovered. The rest are the existing transport.
 */
export type GameCommand = 'start' | 'restart' | 'pause' | 'resume';

type Listener<T> = (payload: T) => void;

export class GameBus {
  private runStart = new Set<Listener<RunStartPayload>>();
  private gameOver = new Set<Listener<GameOverPayload>>();
  private commands = new Set<Listener<GameCommand>>();

  /**
   * React subscribes here to learn the run has begun (the player started dragging
   * the teased ball) so it can animate the menu away. Returns an unsubscribe.
   */
  onRunStart(cb: Listener<RunStartPayload>): () => void {
    this.runStart.add(cb);
    return () => this.runStart.delete(cb);
  }

  /** Phaser ({@link StartFlowScene}) calls this exactly once when a run begins. */
  emitRunStart(payload: RunStartPayload): void {
    this.runStart.forEach((cb) => cb(payload));
  }

  /** React subscribes here to react to the end of a run. Returns an unsubscribe. */
  onGameOver(cb: Listener<GameOverPayload>): () => void {
    this.gameOver.add(cb);
    return () => this.gameOver.delete(cb);
  }

  /** Phaser calls this exactly once when a run ends. */
  emitGameOver(payload: GameOverPayload): void {
    this.gameOver.forEach((cb) => cb(payload));
  }

  /** Phaser (the scene) subscribes here to receive UI commands. */
  onCommand(cb: Listener<GameCommand>): () => void {
    this.commands.add(cb);
    return () => this.commands.delete(cb);
  }

  /** React sends discrete commands to the running game. */
  sendCommand(cmd: GameCommand): void {
    this.commands.forEach((cb) => cb(cmd));
  }

  dispose(): void {
    this.runStart.clear();
    this.gameOver.clear();
    this.commands.clear();
  }
}
